<template>
  <div
    class="fixed top-0 left-0 right-0 z-[55] flex flex-col items-center justify-center h-screen overflow-hidden bg-black/50"
    :class="maxWidth != 'full' ? 'p-6' : ''">
    <div :class="propsClass">
      <BaseDrawerConfirm
        :is-active="modalCloseWarningActive"
        @confirm="emit('dismissChanges')"
        @cancel="onContinueEdit" />

      <button
        type="button"
        @click="onCloseModal"
        class="absolute right-2.5 top-3 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-700 hover:bg-gray-200 hover:text-gray-900">
        <IconXmark class="w-6 h-6" />
      </button>

      <slot name="title">
        <h3 class="py-2 text-xl leading-6 text-gray-900 break-words whitespace-normal pr-10">
          <span v-if="title">{{ title }}</span>
          <span v-else
            >{{ mode === 'add' ? $t('common.actions.create') : $t('common.actions.edit') }}
          </span>
        </h3>
      </slot>

      <form v-if="$slots.form" class="grid w-full overflow-y-auto gap-y-5">
        <slot name="form" />
      </form>

      <div class="flex items-center justify-end w-full gap-2 mt-2" v-if="!disable">
        <BaseButton outline size="sm" @click="onCloseModal">{{
          $t('common.actions.cancel')
        }}</BaseButton>
        <BaseButton @click="emit('save')" size="sm" class="capitalize" v-if="!hideSaveButton">
          <slot name="saveButtonText"
            >{{ mode === 'select' ? $t('common.actions.select') : $t('common.actions.save') }}
          </slot>
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue';
import BaseButton from './BaseButton.vue';
import BaseDrawerConfirm from './BaseDrawerConfirm.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import { onBeforeRouteLeave } from 'vue-router';
import { computed } from 'vue';
import { cva } from 'class-variance-authority';

const props = withDefaults(
  defineProps<{
    mode?: 'add' | 'edit' | 'select' | 'assign' | 'view';
    isModified: boolean;
    disable?: boolean;
    title?: string;
    hideSaveButton?: boolean;
    maxWidth?: 'compact' | 'narrow' | 'wide' | 'extra' | 'full' | 'fullMargin';
  }>(),
  {
    maxWidth: 'compact',
  }
);

const propsClass = computed(() => {
  return cva('relative grid grid-rows-[auto_1fr_auto] w-full gap-4 p-8 bg-white shadow-md', {
    variants: {
      maxWidth: {
        compact: 'max-w-2xl h-auto rounded-lg',
        narrow: 'max-w-sm h-auto rounded-lg',
        wide: 'max-w-4xl h-full rounded-lg',
        extra: 'max-w-7xl h-full rounded-lg',
        full: 'w-full h-full',
        fullMargin: 'w-full h-full m-px rounded-lg',
      },
    },
  })({
    maxWidth: props.maxWidth,
  });
});

const emit = defineEmits(['dismissChanges', 'save']);

const modalCloseWarningActive = ref(false);

function onCloseModal() {
  if (props.isModified) modalCloseWarningActive.value = true;
  else emit('dismissChanges');
}

function onContinueEdit() {
  modalCloseWarningActive.value = false;
}

onBeforeRouteLeave(() => {
  if (props.isModified) {
    const value = window.confirm('Changes that you made may not be saved.');
    if (value) return true;
    else return false;
  }
  window.removeEventListener('beforeunload', onUnload);
});

onBeforeMount(() => {
  window.addEventListener('beforeunload', onUnload);
});

function onUnload(e: any) {
  if (props.isModified) {
    e.stopPropagation();
    e.preventDefault();
  }
}
</script>
